<template>
    <div v-if="show" class="modal-overlay" @click.self="closeModal">
        <div class="modal-container">
            <div class="modal-header">
                <h3>Ouvrir une caisse</h3>
                <BaseButton class="modal-close" @click="closeModal">
                    <i class="fas fa-times" style="width: 20px; height: 20px;"></i>
                </BaseButton>
            </div>

            <div class="modal-body">
                <form @submit.prevent="submitForm">
                    <div class="form-group">
                        <label for="startBalance">Solde initial*</label>
                        <div class="input-group">
                            <div class="input-prefix">FCFA</div>
                            <input id="startBalance" v-model="form.start_balance" type="number" min="0" step="1"
                                placeholder="0" class="form-control"
                                :class="{ 'input-error': validationErrors.start_balance }" required />
                        </div>
                        <span v-if="validationErrors.start_balance" class="field-error">
                            {{ validationErrors.start_balance[0] }}
                        </span>
                    </div>

                    <div class="form-group">
                        <label for="note">Remarque (optionnelle)</label>
                        <textarea id="note" v-model="form.note" placeholder="Ajoutez une remarque..."
                            class="form-control" :class="{ 'input-error': validationErrors.note }" rows="4"></textarea>
                        <span v-if="validationErrors.note" class="field-error">
                            {{ validationErrors.note[0] }}
                        </span>
                    </div>

                    <div class="modal-footer">
                        <BaseButton type="button" class="btn btn-secondary" @click="closeModal" :disabled="isSubmitting">
                            Annuler
                        </BaseButton>
                        <BaseButton type="submit" class="btn btn-primary" :disabled="isSubmitting">
                            <span v-if="isSubmitting" class="spinner-small"></span>
                            <span v-else>Ouvrir la caisse</span>
                        </BaseButton>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch } from 'vue'
import CashRegisterService from '@/services/cashRegisterService'
import { useToastStore } from '@/store/modules/toastStore'

export default {
    name: 'OpenCashRegisterModal',
    props: {
        show: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close', 'success'],
    setup(props, { emit }) {
        const toastStore = useToastStore()

        const form = ref({
            start_balance: '',
            note: ''
        })

        const validationErrors = ref({})
        const isSubmitting = ref(false)

        const resetForm = () => {
            form.value = {
                start_balance: '',
                note: ''
            }
            validationErrors.value = {}
        }

        const closeModal = () => {
            if (!isSubmitting.value) {
                resetForm()
                emit('close')
            }
        }

        const submitForm = async () => {
            if (isSubmitting.value) return

            validationErrors.value = {}
            isSubmitting.value = true

            try {
                const data = {
                    start_balance: parseFloat(form.value.start_balance),
                    note: form.value.note || null
                }

                await CashRegisterService.openCashRegister(data)

                toastStore.addToast({
                    type: 'success',
                    message: 'Caisse ouverte avec succès',
                    duration: 4000
                })

                await CashRegisterService.pullAndSaveOpenedCashRegister();

                resetForm()
                emit('success')
                emit('close')
            } catch (error) {
                if (error.status === 422 && error.message) {
                    toastStore.addToast({
                        type: 'error',
                        message: error.message,
                        duration: 4000
                    })
                } else {
                    toastStore.addToast({
                        type: 'error',
                        message: error.message || 'Erreur lors de l\'ouverture de la caisse',
                        duration: 4000
                    })
                }
            } finally {
                isSubmitting.value = false
            }
        }

        watch(() => props.show, (newVal) => {
            if (newVal) {
                resetForm()
            }
        })

        return {
            form,
            validationErrors,
            isSubmitting,
            closeModal,
            submitForm
        }
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
}

.modal-container {
    background: white;
    border-radius: 0.75rem;
    width: 100%;
    max-width: 500px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}

.modal-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #718096;
    padding: 0.5rem;
    border-radius: 0.375rem;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-close:hover {
    background: #f7fafc;
    color: #2d3748;
}

.modal-body {
    padding: 1.5rem;
    overflow-y: auto;
}

.form-group {
    margin-bottom: 1.5rem;
}

.form-group:last-child {
    margin-bottom: 0;
}

.form-group label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #4a5568;
    margin-bottom: 0.5rem;
}

.input-group {
    display: flex;
    align-items: center;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    overflow: hidden;
    transition: all 0.2s;
}

.input-group:focus-within {
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.input-prefix {
    background: #f7fafc;
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: #4a5568;
    border-right: 1px solid #e2e8f0;
}

.form-control {
    width: 100%;
    padding: 0.625rem 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    transition: all 0.2s;
    font-family: inherit;
}

.input-group .form-control {
    border: none;
    border-radius: 0;
}

.input-group .form-control:focus {
    outline: none;
    box-shadow: none;
}

.form-control:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.form-control.input-error {
    border-color: #fc8181;
}

.input-group.input-error {
    border-color: #fc8181;
}

textarea.form-control {
    resize: vertical;
    min-height: 80px;
}

.field-error {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #e53e3e;
}

.modal-footer {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    padding-top: 1.5rem;
    margin-top: 1.5rem;
    border-top: 1px solid #e2e8f0;
}

.btn {
    padding: 0.625rem 1.5rem;
    border: none;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-secondary {
    background: #f7fafc;
    color: #4a5568;
    border: 1px solid #e2e8f0;
}

.btn-secondary:hover:not(:disabled) {
    background: #edf2f7;
    border-color: #cbd5e0;
}

.btn-primary {
    background: linear-gradient(135deg, #0A5C6B 0%, #094854 100%);
    color: white;
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10, 92, 107, 0.4);
}

.spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 640px) {
    .modal-container {
        max-width: 100%;
        margin: 1rem;
    }

    .modal-footer {
        flex-direction: column-reverse;
    }

    .btn {
        width: 100%;
    }
}
</style>
